import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Home, ArrowUp } from "lucide-react";
import { useState, useEffect, useRef } from "react";

export function BackHomeButton() {
  const location = useLocation();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const isHomePage = location.pathname === "/";
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      // Use requestAnimationFrame to batch layout reads and reduce reflow
      if (rafRef.current) return;
      
      rafRef.current = requestAnimationFrame(() => {
        setShowScrollTop(window.scrollY > 400);
        rafRef.current = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <AnimatePresence>
      {(showScrollTop || !isHomePage) && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-6 right-6 z-50 flex flex-col gap-3"
        >
          {/* Scroll to top button */}
          {showScrollTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToTop}
              className="w-12 h-12 rounded-full bg-card border border-border shadow-lg flex items-center justify-center text-foreground hover:bg-accent hover:text-accent-foreground transition-colors group"
            >
              <ArrowUp className="h-5 w-5 group-hover:animate-bounce" />
            </motion.button>
          )}

          {/* Back home button (only show if not on home page) */}
          {!isHomePage && (
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-accent text-accent-foreground font-medium shadow-accent hover:shadow-glow transition-all duration-300 group"
              >
                <Home className="h-5 w-5" />
                <span className="hidden sm:inline">Back Home</span>
              </Link>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
